import { __awaiter, __decorate } from "tslib";
import { Auth, Connect } from '@genesislcap/foundation-comms';
import { showNotification } from '@genesislcap/foundation-notifications';
import { LifecycleMixin } from '@genesislcap/foundation-utils';
import { GridProRendererTypes } from '@genesislcap/foundation-zero-grid-pro';
import { attr, css, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { getErrorFormat, logger } from '../utils';
import { UsersTemplate } from './users.template';
const defaultColumnConfig = {
    enableCellChangeFlash: true,
    enableRowGroup: false,
    enablePivot: true,
    enableValue: true,
};
/**
 * Defines the default columns which are present on the user management component
 *
 * @remarks
 *
 * This is exported so you can override and mix-in custom columns along with the default
 * @public
 */
export const UsersColumnConfig = [
    Object.assign(Object.assign({}, defaultColumnConfig), { field: 'USER_NAME', headerName: 'Username' }),
    Object.assign(Object.assign({}, defaultColumnConfig), { field: 'FIRST_NAME', headerName: 'First Name' }),
    Object.assign(Object.assign({}, defaultColumnConfig), { field: 'LAST_NAME', headerName: 'Last Name' }),
    Object.assign(Object.assign({}, defaultColumnConfig), { field: 'EMAIL_ADDRESS', headerName: 'Email' }),
    Object.assign(Object.assign({}, defaultColumnConfig), { field: 'LAST_LOGIN', headerName: 'Last Login', valueFormatter: ({ value }) => value
            ? new Intl.DateTimeFormat('default', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            }).format(value)
            : '' }),
];
/**
 * Main class which defines the user management functionality
 *
 * @remarks
 *
 * Under the hood the user-management is the entity-management micro-frontend configured for use
 * with the `ALL_USERS` resource.
 *
 * @public
 *
 * @example
 *
 * With no customisation required the user management micro-frontend can simply be dropped into the
 * template as shown. However, it is advised to wrap it in an error boundary component.
 * ```javascript
 * <user-management></user-management>
 * ```
 * With this default example above the columns will be the {@link UsersColumnConfig} as well as the
 * entity and status columns. The entity and status
 * columns are always added regardless of the other columns which are configured.
 *
 * You can add in additional customisation options. For example you can define a custom column and
 * then mix-in with the standard columns
 * ```javascript
 * import { Users, UsersColumnConfig, } from '@genesislcap/foundation-entity-management';
 *
 * const userColumns = [
 *   {
 *     field: 'ACCESS_TYPE',
 *     headerName: 'Access Type',
 *   },
 * ];
 *
 * // Definition inside of the html template
 * <user-management :columns=${() => [...UsersColumnConfig, ...userColumns]}></user-management>
 * ```
 */
let Users = class Users extends LifecycleMixin(FASTElement) {
    /**
     * Returns a configuration for an optional status column
     *
     * @returns Column configuration for an optional status column
     * @internal
     */
    statusColumn() {
        return Object.assign(Object.assign({}, defaultColumnConfig), { headerName: 'Status', field: 'STATUS', minWidth: 135, width: 135, suppressSizeToFit: true, cellRendererSelector: this.statusRendererSelector.bind(this) });
    }
    /**
     * Returns the config for an entity column if the entityID attribute is defined
     *
     * @returns Config for an entity column if the entityID is defined, else an empty array
     * @internal
     */
    entityColumn() {
        return this.entityID
            ? [
                Object.assign(Object.assign({}, defaultColumnConfig), { headerName: this.entityLabel, field: this.entityID }),
            ]
            : [];
    }
    /**
     * Constructor will set the {@link Users.createEvent} {@link Users.deleteEvent} {@link Users.updateEvent} to match the permissions set from the server
     */
    constructor() {
        super();
        /**
         * Configuring the resourceName from the entity-management, set to manage the users resource specifically
         * @internal
         */
        this.resourceName = 'ALL_USERS';
        /**
         * This attribute controls whether and how the user manager stores the state of the columns when the user edits them. Defaulted to `entity_users_management`.
         *
         * @remarks
         *
         * Setting this value will set the entity manager to persist the column states through page refreshes etc. An example of what is stored is when the user resizes or reorders columns.
         * This value must be unique for each table in your app otherwise the persisted data will be corrupted.
         * There is an option on the grid for the user to reset the table to the default layout if they wish.
         * @public
         */
        this.persistColumnStateKey = 'entity_users_management';
        /**
         * Column definition, default to the UsersColumnConfig
         */
        this.columns = UsersColumnConfig;
        this.additionalCreateFields = [];
        this.additionalUpdateFields = [];
        /**
         * entityID used to control the definition of an optional entity column which is created with the {@link Users.entityColumn} function.
         * @internal
         */
        this.entityID = 'COUNTERPARTY_ID';
        /**
         * entityID used to display label from the selector of entityID {@link Users.entityColumn} function.
         * @internal
         */
        this.entityLabelKey = 'NAME';
        /**
         * entityID used to control the definition of an optional entity column which is created with the {@link Users.entityColumn} function.
         * @internal
         */
        this.entityLabel = 'Counterparty';
        /**
         * Definition of the `DATASERVER` query which is used when managing Counterpartys
         * @internal
         */
        this.availableEntitiesEndpoint = 'ALL_COUNTERPARTYS';
        this.createEvent = this.auth.currentUser.hasPermission('INSERT_USER')
            ? 'EVENT_INSERT_USER'
            : '';
        this.deleteEvent = this.auth.currentUser.hasPermission('DELETE_USER')
            ? 'EVENT_DELETE_USER'
            : '';
        this.updateEvent = this.auth.currentUser.hasPermission('AMEND_USER') ? 'EVENT_AMEND_USER' : '';
    }
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.connectedCallback.call(this);
            // retrieving access type of currently logged-in user
            const userReq = yield this.connect.snapshot('ALL_USERS', {
                CRITERIA_MATCH: `USER_NAME == '${this.auth.currentUser.username}'`,
            });
            if (userReq.ROW[0]) {
                this.allAccessType = userReq.ROW[0].ACCESS_TYPE === 'ALL';
                this.entityIdValue = userReq.ROW[0][this.entityID];
            }
        });
    }
    deepClone() {
        const copy = super.deepClone();
        copy.additionalCreateFields = structuredClone(this.additionalCreateFields);
        copy.additionalUpdateFields = structuredClone(this.additionalUpdateFields);
        copy.allAccessType = structuredClone(this.allAccessType);
        copy.createEvent = structuredClone(this.createEvent);
        copy.datasourceConfig = structuredClone(this.datasourceConfig);
        copy.deleteEvent = structuredClone(this.deleteEvent);
        copy.updateEvent = structuredClone(this.updateEvent);
        // this.columns[number] might contain a function, so can't use structuredClone
        copy.columns = this.columns ? [...this.columns].map((x) => (Object.assign({}, x))) : this.columns;
        return copy;
    }
    /**
     * Used as the callback function in {@link Users.statusColumn}
     * @internal
     */
    statusRendererSelector(params) {
        return {
            component: GridProRendererTypes.action,
            params: {
                actionClick: () => this.changeStatus(params),
                actionName: params.value === 'ENABLED' ? 'Deactivate' : 'Activate',
                appearance: 'secondary-orange',
            },
        };
    }
    /**
     * Changes the status of an entity
     *
     * @remarks
     *
     * The action which is run on the status button from {@link Users.statusRendererSelector}
     *
     * @internal
     */
    changeStatus(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const isEnabled = params.data.STATUS === 'ENABLED';
            // optimistically set value and store previous value
            const previousValue = params.data.STATUS;
            params.setValue(isEnabled ? 'DISABLED' : 'ENABLED');
            const changeStatusRequest = yield this.connect.commitEvent(isEnabled ? 'EVENT_DISABLE_USER' : 'EVENT_ENABLE_USER', {
                DETAILS: {
                    USER_NAME: params.data.USER_NAME,
                },
            });
            if (changeStatusRequest.ERROR) {
                logger.error(changeStatusRequest.ERROR);
                const notificationCloseTimeout = 5000;
                changeStatusRequest.ERROR.forEach((err) => {
                    showNotification({
                        title: err.CODE,
                        body: getErrorFormat(err),
                        config: {
                            toast: { type: 'warning', autoClose: true, closeTimeout: notificationCloseTimeout },
                        },
                    }, 'zero');
                });
                // bring back original value
                params.setValue(previousValue);
            }
        });
    }
    /**
     * @internal
     */
    readUserData(user) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const userProfiles = yield this.connect.request('PROFILE_USER', {
                REQUEST: {
                    USER_NAME: user.USER_NAME,
                },
            });
            const defaultValues = !this.allAccessType && ((_a = this.entityIdValue) === null || _a === void 0 ? void 0 : _a.length)
                ? {
                    ACCESS_TYPE: 'ENTITY',
                    [this.entityID]: this.entityIdValue,
                }
                : {};
            return Object.assign(Object.assign({}, defaultValues), { USER_PROFILES: (userProfiles.REPLY || []).map((profile) => profile.PROFILE_NAME), ADDRESS_LINE_1: user.ADDRESS_LINE1, ADDRESS_LINE_2: user.ADDRESS_LINE2, ADDRESS_LINE_3: user.ADDRESS_LINE3, ADDRESS_LINE_4: user.ADDRESS_LINE4 });
        });
    }
};
__decorate([
    Connect
], Users.prototype, "connect", void 0);
__decorate([
    Auth
], Users.prototype, "auth", void 0);
__decorate([
    observable
], Users.prototype, "datasourceConfig", void 0);
__decorate([
    attr
], Users.prototype, "resourceName", void 0);
__decorate([
    attr({ attribute: 'persist-column-state-key' })
], Users.prototype, "persistColumnStateKey", void 0);
__decorate([
    observable
], Users.prototype, "columns", void 0);
__decorate([
    observable
], Users.prototype, "additionalCreateFields", void 0);
__decorate([
    observable
], Users.prototype, "additionalUpdateFields", void 0);
__decorate([
    observable
], Users.prototype, "allAccessType", void 0);
__decorate([
    observable
], Users.prototype, "createEvent", void 0);
__decorate([
    observable
], Users.prototype, "deleteEvent", void 0);
__decorate([
    observable
], Users.prototype, "updateEvent", void 0);
__decorate([
    attr
], Users.prototype, "entityID", void 0);
__decorate([
    attr
], Users.prototype, "entityLabelKey", void 0);
__decorate([
    attr
], Users.prototype, "entityLabel", void 0);
__decorate([
    attr
], Users.prototype, "availableEntitiesEndpoint", void 0);
Users = __decorate([
    customElement({
        name: 'user-management',
        template: UsersTemplate,
        styles: css `
    :host {
      height: 100%;
    }
  `,
    })
], Users);
export { Users };
